import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuthService } from '@services/auth';
import { FeedbackService } from '@services/feedback';
import { FormsService } from '@services/forms';
import { ILegalUploader, isISortedTerm, ISortedTerm, ITermResponse, LegalsService, TermType } from '@services/legals';
import { ProgressIndicatorService } from '@services/progress';
import { I18nService, StaticService } from '@src/app/i18n/services';
import { OnDestroyMixin } from '@w11k/ngx-componentdestroyed';
import isNil from 'lodash/isNil';

import { fileSizeDisplay, validVersion, versionCompare, versionToKey } from '../../../../../../../shared/utilities';
import { CompanyService } from '../../../../../../services/company';
import { FormEditorInputType } from '../../../../../../shared/components';
import { NewVersionLegal } from './legal-new-version.models';

enum FileTypes {
	url = 'url',
	upload = 'upload'
}

@Component({
	selector: 'app-legal-new-version',
	templateUrl: './legal-new-version.dialog.html'
})
export class LegalNewVersionDialogComponent extends OnDestroyMixin implements OnInit {
	public formInputs = new FormGroup({
		name: new FormControl('', [Validators.required]),
		url: new FormControl('', [], [this.formsService.urlValid()]),
		locale: new FormControl('', [Validators.required]),
		version: new FormControl('', [Validators.required]),
		active: new FormControl(false, [Validators.required]),
		fileType: new FormControl(FileTypes.url)
	});

	public clusterId: string;
	public fileTypes = FileTypes;
	public formEditorInputType = FormEditorInputType;
	public locales: any[] = [];
	public statuses = [
		{ value: true, label: this.i18nService.get('publish', 'app.descriptor.publish') },
		{ value: false, label: this.i18nService.get('draft', 'app.descriptor.draft') }
	];
	public currentVersion: string;
	public maxSize = 30000000; // 30MB
	public isAdmin = false;
	public termType = TermType;

	get formIsValid(): boolean {
		if (this.getVersionError()) {
			return false;
		}

		return this.formInputs.valid;
	}

	constructor(
		private legalsService: LegalsService,
		private companyService: CompanyService,
		private i18nService: I18nService,
		private staticService: StaticService,
		private progressIndicatorService: ProgressIndicatorService,
		private feedbackService: FeedbackService,
		private formsService: FormsService,
		@Inject(MAT_DIALOG_DATA) public dialogData: NewVersionLegal,
		private dialogRef: MatDialogRef<LegalNewVersionDialogComponent>
	) {
		super();
	}

	public ngOnInit(): void {
		this.isAdmin = this.companyService.isAdminContact();
		this.locales = this.dialogData?.locales
			? this.dialogData.locales
			: this.staticService.get('locales').map((locale) => {
					return {
						value: locale.id,
						label: locale.label
					};
			  });

		if (this.dialogData['cluster']) {
			this.clusterId = this.dialogData.cluster.clusterId;
		}

		this.formInputs.get('locale').valueChanges.subscribe((locale: string) => {
			this.populateName(locale);
		});

		if (isISortedTerm(this.dialogData.terms)) {
			this.currentVersion = this.dialogData.terms.version;
		} else {
			this.formInputs.get('locale').clearValidators();
			this.formInputs.get('locale').updateValueAndValidity();

			this.formInputs.get('name').clearValidators();
			this.formInputs.get('name').updateValueAndValidity();

			this.currentVersion = this.dialogData.terms.documents[this.dialogData.terms.latestVersion].displayVersion;
		}

		this.formInputs.get('version').setValue(this.currentVersion);

		this.formInputs.get('fileType').valueChanges.subscribe((value) => {
			this.formInputs.get('url').setValidators([Validators.required]);
			this.formInputs.get('url').updateValueAndValidity();
		});
	}

	private populateName(locale: string): void {
		const nameInput = this.formInputs.get('name');

		if (nameInput.untouched) {
			let name = '';

			if (isISortedTerm(this.dialogData.terms)) {
				const terms: ISortedTerm = this.dialogData.terms;

				terms.versionHistory.forEach((version) => {
					if (terms.versions[versionToKey(version)][locale]) {
						name = terms.versions[versionToKey(version)][locale].name;
					} else if (!name && terms.versions[versionToKey(version)]['en']) {
						name = terms.versions[versionToKey(version)]['en'].name;
					}
				});
			}

			if (name) {
				nameInput.setValue(name);
				nameInput.markAsUntouched();
			}
		}
	}

	public getVersionError(): string {
		const version = this.formInputs.value.version ? this.formInputs.value.version + '' : '';

		if (isISortedTerm(this.dialogData.terms)) {
			if (version) {
				const compared = versionCompare(version, this.currentVersion);

				if (!validVersion(version)) {
					return this.i18nService.get('Not valid', 'app.form.input.version.invalid');
				} else if (compared < 0) {
					return this.i18nService.get('Enter higher version', 'app.form.input.version.must-be-higher');
				} else if (compared === 0) {
					const versionKey = versionToKey(version);

					if (!isNil(this.dialogData.terms.versions?.[versionKey]?.[this.formInputs.controls.locale.value])) {
						return this.i18nService.get('Already existing language version', 'app.form.input.version.locale-already-exists');
					}
				}
			}
		} else {
			let exists = false;

			Object.values(this.dialogData.terms.documents).forEach((document) => {
				if (document.displayVersion === version) {
					exists = true;
				}
			});

			if (exists) return this.i18nService.get('Enter higher version', 'app.form.input.version.must-be-higher');
		}

		if (version.length === 0) {
			return this.i18nService.get('Not valid', 'app.form.input.version.invalid');
		}

		return null;
	}

	public filename(file: any): string {
		return file ? file.name : '';
	}

	public filesize(file: any): string {
		if (file) {
			const size = file.size;
			return fileSizeDisplay(size);
		}
		return '';
	}

	public cancel(): void {
		if (this.dialogData.callback) {
			this.dialogData.callback(null);
		}
	}

	public onFormSubmit(): void {
		const legal: ILegalUploader = {
			name: this.formInputs.value.name,
			version: this.formInputs.value.version ? String(this.formInputs.value.version) : '1.0',
			active: this.formInputs.value.active
		};

		legal.url = this.formInputs.value.url;

		if (isISortedTerm(this.dialogData.terms)) {
			legal['id'] = this.dialogData.terms.id;
			legal['locale'] = this.formInputs.value.locale ? this.formInputs.value.locale : this.dialogData.locale;
		} else {
			legal['id'] = this.dialogData.terms.termsId;
		}

		this.progressIndicatorService.spinner(true);

		this.legalsService.addLegalDocument(legal, this.dialogData.type, this.clusterId).subscribe(
			(result: ITermResponse) => {
				this.progressIndicatorService.spinner(false);
				if (this.dialogData.callback) {
					this.dialogData.callback(result[this.dialogData.type]);
				}
				this.dialogRef.close();
			},
			(error: any) => {
				if (this.dialogData.callback) {
					this.dialogData.callback(null);
				}
				this.feedbackService.apiError(error);
				this.progressIndicatorService.spinner(false);
			}
		);
	}
}
